import { FC } from "react";
import __ from "src/utils/Translations";
import { Controller, useForm, SubmitHandler } from "react-hook-form";
import {
  Box,
  Button,
  Paper,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  useTheme,
  Stack,
} from "@mui/material";
import { CloseOutlined } from "@mui/icons-material";
import SearchIcon from "src/Pages/Company/OrganizationStructure/sharedComponents/SearchIcon";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { Link } from "react-router-dom";
import { styles } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/styles";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { organizationStructureAreaAdd } from "src/utils/RoleBasedSecurity/permissions";
import useIsAhr from "src/utils/hooks/useIsAhr";
import { FilterBarSchema } from "./StructureAreas.scheme";
import { IAreasQueryParams, IFilterBar } from "./StructureAreas.models";
import Area from "./DataManagment/Area/index";

const FilterBar: FC<IFilterBar> = ({ updateQueryParameters }) => {
  const isAhr = useIsAhr();
  const theme = useTheme();

  const {
    handleSubmit,
    control,
    resetField,
    formState: { errors },
  } = useForm<IAreasQueryParams>({
    resolver: standardSchemaResolver(FilterBarSchema),
    defaultValues: { search: "", sortType: "asc" },
  });

  const companyId = useCompanyId();
  const hasPermissionStructureAreaAdd = usePermission(
    organizationStructureAreaAdd
  );

  const onSubmit: SubmitHandler<IAreasQueryParams> = (data) => {
    updateQueryParameters(data);
  };

  return (
    <Paper
      component="form"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      sx={{ p: 2 }}
    >
      <Box
        sx={{
          ...styles.FilterBar.root,
          [theme.breakpoints.down("md")]: {
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
          },
        }}
      >
        <Controller
          name="search"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSubmit(onSubmit)();
                  field.onBlur();
                }
              }}
              slotProps={{
                inputLabel: {
                  ...(!(field.value.length > 0) && {
                    sx: { padding: "0 0 0 32px" },
                  }),
                  shrink: field.value.length > 0,
                },
                input: {
                  startAdornment: (
                    <InputAdornment sx={{ cursor: "pointer" }} position="start">
                      <Box
                        onClick={() => {
                          handleSubmit(onSubmit)();
                        }}
                      >
                        <SearchIcon />
                      </Box>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment sx={{ cursor: "pointer" }} position="end">
                      {field.value !== "" && (
                        <CloseOutlined
                          onClick={() => {
                            resetField("search");
                            handleSubmit(onSubmit)();
                          }}
                        />
                      )}
                    </InputAdornment>
                  ),
                },
              }}
              label={__("Szukaj")}
              error={!!errors.search}
              helperText={errors.search?.message}
              sx={{
                minWidth: "300px",
                [theme.breakpoints.down("md")]: {
                  width: "100%",
                },
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="sortType"
          render={({ field }) => (
            <Select
              {...field}
              sx={{
                minWidth: "257px",
                [theme.breakpoints.down("md")]: {
                  width: "100%",
                },
              }}
              onChange={(e) => {
                field.onChange(e);
                handleSubmit(onSubmit)();
              }}
              value={field.value}
              variant="outlined"
              error={!!errors.sortType}
            >
              <MenuItem value="asc">A-Z</MenuItem>
              <MenuItem value="desc">Z-A</MenuItem>
            </Select>
          )}
        />
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "flex-end",
            flexBasis: "100%",
            gap: 2,
          }}
        >
          <Area />
          {hasPermissionStructureAreaAdd && (
            <Button
              component={Link}
              to={
                isAhr
                  ? `/ahr/organization-structure/configure-structure/create-area`
                  : `/company/edit/${companyId}/organization-structure/configure-structure/create-area`
              }
              size="large"
              variant="contained"
              sx={{
                [theme.breakpoints.down("md")]: {
                  width: "100%",
                },
              }}
            >
              {__("Dodaj obszar")}
            </Button>
          )}
        </Stack>
      </Box>
    </Paper>
  );
};

export default FilterBar;
